import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Wrench, Droplets, Bike, ArrowRight, MapPin, Heart, MessageCircle,
  Star, Waves, Leaf, CheckCircle2, Quote, Sparkles,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { GoogleBadge } from "../components/GoogleBadge";
import { MotoCard } from "../components/MotoCard";
import { Reveal, RevealStagger, revealItem } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import { ReviewsCarousel } from "../components/ReviewsCarousel";
import { Typewriter } from "../components/Typewriter";
import { BlockReveal } from "../components/BlockReveal";
import { HeroVideoBg } from "../components/HeroVideoBg";
import {
  SITE, whatsappLink, CAR_WASH_SERVICES, MAINTENANCE_SERVICES, LOCATIONS, embedSrc,
} from "../data/site";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const HERO_VIDEOS = ["/images/hero-1-web.mp4", "/images/hero-2-web.mp4"];
const HERO_POSTER = "/images/gallery/fb-beach-bikes.jpg";

export default function Home() {
  const { t } = useLang();
  const [motos, setMotos] = useState([]);
  const [summary, setSummary] = useState([]);

  useEffect(() => {
    api.get("/motorcycles").then((r) => setMotos(r.data)).catch(() => {});
    api.get("/motorcycles/availability").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const featured = motos.filter((m) => m.featured).slice(0, 6);
  const totalAvailable = summary.reduce((a, s) => a + s.available, 0);

  const DepositButtons = ({ className = "" }) => (
    <div className={`flex flex-col sm:flex-row sm:flex-wrap gap-3 ${className}`}>
      {[25, 50].map((amt) => (
        <motion.a
          key={amt}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.97 }}
          href={whatsappLink(t.wa.deposit(amt))}
          target="_blank"
          rel="noreferrer"
          data-testid={`hero-deposit-${amt}-button`}
          className="inline-flex items-center justify-center w-full sm:w-auto gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-heading font-bold py-3 px-6 rounded-full transition-colors shadow-lg"
        >
          <MessageCircle size={18} /> ${amt}.00 {t.common.deposit}
        </motion.a>
      ))}
    </div>
  );

  return (
    <div className="App">
      <Navbar />

      {/* ===================== HERO (VIDEO) ===================== */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <HeroVideoBg sources={HERO_VIDEOS} poster={HERO_POSTER} />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/90 via-brand-blue/70 to-brand-blue/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-6">
              <GoogleBadge dark />
            </motion.div>
            <motion.span
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 text-brand-sky font-heading font-bold uppercase tracking-[0.2em] text-sm"
            >
              <MapPin size={16} /> {t.hero.location}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]"
            >
              <Typewriter
                speed={45}
                startDelay={500}
                segments={[
                  { text: t.hero.titleLine1 + " " },
                  { text: t.hero.titleLine2, className: "block text-brand-orange" },
                ]}
              />
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
              className="text-white/90 text-lg mt-5 max-w-xl leading-relaxed"
            >
              <span className="font-heading font-bold text-white">{t.hero.subtitleBold}</span> {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-8"
            >
              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
                <Link to="/motorcycles" data-testid="hero-browse-button" className="flex w-full sm:w-auto justify-center items-center gap-2 bg-brand-orange hover:bg-brand-orange/90 text-white font-heading font-bold py-3 px-7 rounded-full transition-colors shadow-lg">
                  <Bike size={18} /> {t.hero.rentMoto}
                </Link>
              </motion.div>
              <motion.a whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }}
                href={whatsappLink(t.wa.general)}
                target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button"
                className="flex w-full sm:w-auto justify-center items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur text-white font-heading font-bold py-3 px-7 rounded-full transition-colors"
              >
                <MessageCircle size={18} /> {t.hero.chatWhatsapp}
              </motion.a>
              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
                <Link to="/locations" data-testid="hero-directions-button"
                  className="flex w-full sm:w-auto justify-center items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur text-white font-heading font-bold py-3 px-7 rounded-full transition-colors"
                >
                  <MapPin size={18} /> {t.hero.getDirections}
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== INTRO STRIP ===================== */}
      <section className="bg-white py-14">
        <Reveal className="max-w-4xl mx-auto px-4 text-center">
          <Sparkles className="text-brand-orange mx-auto mb-4" size={28} />
          <p className="font-heading font-bold text-2xl sm:text-3xl text-brand-gray leading-snug text-balance">
            <Typewriter
              speed={26}
              segments={[
                { text: t.intro.text1 + " " },
                { text: t.intro.text2, className: "text-brand-blue" },
              ]}
            />
          </p>
        </Reveal>
      </section>

      {/* ===================== TRUST / STATS ===================== */}
      <section className="bg-brand-gray text-white">
        <RevealStagger className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {[
            { icon: Heart, value: SITE.yearsInBusiness, suffix: "", sub: t.stats.years },
            { icon: Bike, value: motos.length || 14, suffix: "+", sub: t.stats.models },
            { icon: CheckCircle2, value: totalAvailable || 0, suffix: "", sub: t.stats.availableNow },
            { icon: Star, value: 5, suffix: ".0", sub: t.google.rating },
          ].map((s, i) => (
            <motion.div key={i} variants={revealItem} className="py-9 px-4 text-center flex flex-col items-center gap-2">
              <s.icon className="text-brand-orange" size={26} />
              <span className="font-heading font-black text-3xl"><CountUp to={s.value} suffix={s.suffix} /></span>
              <span className="text-white/60 text-sm">{s.sub}</span>
            </motion.div>
          ))}
        </RevealStagger>
      </section>

      {/* ===================== CUSTOMERS FEEDBACK (sliding, above About) ===================== */}
      <ReviewsCarousel />

      {/* ===================== ABOUT (moved above How It Works) ===================== */}
      <section className="py-20 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <Reveal x={-40} y={0}>
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.about.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2">
              <Typewriter speed={34} segments={[{ text: t.about.title }]} />
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">{t.about.body}</p>
            <div className="flex flex-wrap gap-6 mt-8">
              {t.about.points.map((pt) => (
                <span key={pt} className="inline-flex items-center gap-2 text-brand-gray font-heading font-semibold">
                  <ShieldCheck size={18} className="text-brand-orange" /> {pt}
                </span>
              ))}
            </div>
          </Reveal>
          <BlockReveal className="rounded-3xl shadow-xl" delay={0.1}>
            <img src="/images/about-store.jpg" alt="Nica Moto Rentals storefront in San Juan del Sur" className="w-full h-[420px] object-cover" />
          </BlockReveal>
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.howItWorks.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2"><Typewriter speed={34} segments={[{ text: t.howItWorks.title }]} /></h2>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.howItWorks.steps.map((s, i) => (
              <motion.div key={i} variants={revealItem} whileHover={{ y: -8 }}
                className="relative bg-white border border-slate-100 rounded-2xl p-7 shadow-sm hover:shadow-xl transition-shadow"
              >
                <span className="font-heading font-black text-5xl text-brand-sky/25">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading font-bold text-lg text-brand-gray mt-3">{s.title}</h3>
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* ===================== OUR COLLECTION ===================== */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.collection.tag}</span>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2"><Typewriter speed={34} segments={[{ text: t.collection.title }]} /></h2>
            </div>
            <Link to="/motorcycles" className="inline-flex items-center gap-2 text-brand-blue font-heading font-bold hover:gap-3 transition-all" data-testid="home-view-all-motos">
              {t.common.viewAll} <ArrowRight size={18} />
            </Link>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {featured.map((m) => (
              <motion.div key={m.id} variants={revealItem}><MotoCard moto={m} /></motion.div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* ===================== AVAILABILITY ===================== */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.availability.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2"><Typewriter speed={34} segments={[{ text: t.availability.title }]} /></h2>
          </Reveal>
          <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" stagger={0.08}>
            {summary.map((s) => (
              <motion.div key={s.cc} variants={revealItem} whileHover={{ y: -6 }} data-testid={`availability-${s.cc}`}
                className="bg-white rounded-2xl p-5 text-center shadow-sm border border-slate-100"
              >
                <div className="font-heading font-black text-2xl text-brand-blue">{s.cc}cc</div>
                <div className={`mt-2 font-heading font-bold ${s.available > 0 ? "text-green-600" : "text-red-500"}`}>
                  {s.available} / {s.total}
                </div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">{t.availability.available}</div>
              </motion.div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* ===================== LOCATIONS ===================== */}
      <section className="py-16" data-testid="home-locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.locations.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2"><Typewriter speed={34} segments={[{ text: t.locations.title }]} /></h2>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-7" stagger={0.1}>
            {LOCATIONS.map((loc, i) => (
              <motion.div key={loc.id} variants={revealItem}
                data-testid={`home-location-card-${loc.id}`}
                className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow flex flex-col"
              >
                <div className="aspect-[16/9] w-full bg-slate-100">
                  <iframe title={`${loc.name} map`} src={embedSrc(loc.query)} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-brand-orange font-heading font-bold uppercase tracking-[0.15em] text-xs">{i === 0 ? t.locations.main : t.locations.second}</span>
                  <h3 className="font-heading font-black text-xl text-brand-gray mt-1">{loc.name}</h3>
                  <p className="flex items-start gap-2 text-slate-500 text-sm mt-2"><MapPin size={16} className="text-brand-blue shrink-0 mt-0.5" /> {loc.address}</p>
                  <a href={loc.directions} target="_blank" rel="noreferrer" data-testid={`home-location-directions-${loc.id}`}
                    className="mt-5 inline-flex w-fit items-center gap-2 bg-brand-blue hover:bg-brand-blue/90 text-white font-heading font-bold py-2.5 px-6 rounded-full transition-colors"
                  >
                    <MapPin size={16} /> {t.locations.getDirections}
                  </a>
                </div>
              </motion.div>
            ))}
          </RevealStagger>
          <Reveal className="text-center mt-8">
            <Link to="/locations" data-testid="home-view-locations" className="inline-flex items-center gap-2 text-brand-blue font-heading font-bold hover:gap-3 transition-all">
              {t.locations.title} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>


      {/* ===================== WHY CHOOSE US ===================== */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.why.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2"><Typewriter speed={34} segments={[{ text: t.why.title }]} /></h2>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.why.items.map((f, i) => (
              <motion.div key={i} variants={revealItem} whileHover={{ y: -8 }}
                className="bg-white border-t-4 border-brand-blue rounded-2xl p-7 shadow-sm hover:shadow-xl transition-shadow text-center"
              >
                <div className="w-12 h-12 mx-auto rounded-full bg-brand-sky/15 text-brand-blue flex items-center justify-center mb-4">
                  <CheckCircle2 size={24} />
                </div>
                <h3 className="font-heading font-bold text-lg text-brand-gray">{f.title}</h3>
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* ===================== SURF RACKS + ECO ===================== */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          {[
            { img: "/images/surf-racks.jpeg", icon: Waves, data: t.surf },
            { img: "/images/eco-friendly.png", icon: Leaf, data: t.eco },
          ].map((b, i) => (
            <Reveal key={i} delay={i * 0.12} className="group relative rounded-3xl overflow-hidden shadow-lg min-h-[340px] flex items-end">
              <img src={b.img} alt={b.data.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-gray/90 via-brand-gray/40 to-transparent" />
              <div className="relative p-8 text-white">
                <span className="inline-flex items-center gap-2 bg-brand-orange text-white text-xs font-heading font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-3">
                  <b.icon size={14} /> {b.data.tag}
                </span>
                <h3 className="font-heading font-black text-2xl">{b.data.title}</h3>
                <p className="text-white/85 mt-2 max-w-md">{b.data.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.services.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2"><Typewriter speed={34} segments={[{ text: t.services.title }]} /></h2>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div variants={revealItem}>
              <Link to="/services/car-wash" data-testid="home-carwash-card" className="group block bg-gradient-to-br from-brand-blue to-brand-sky text-white rounded-3xl p-10 relative overflow-hidden hover:shadow-2xl transition-shadow">
                <Droplets size={40} className="mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading font-black text-2xl">{t.services.carwashTitle}</h3>
                <p className="text-white/85 mt-2 max-w-sm">{t.services.carwashDesc} C${CAR_WASH_SERVICES[0].price}.</p>
                <span className="inline-flex items-center gap-2 mt-6 font-heading font-bold group-hover:gap-3 transition-all">{t.common.seePricing} <ArrowRight size={18} /></span>
              </Link>
            </motion.div>
            <motion.div variants={revealItem}>
              <Link to="/services/maintenance" data-testid="home-maintenance-card" className="group block bg-gradient-to-br from-brand-orange to-amber-500 text-white rounded-3xl p-10 relative overflow-hidden hover:shadow-2xl transition-shadow">
                <Wrench size={40} className="mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading font-black text-2xl">{t.services.maintTitle}</h3>
                <p className="text-white/90 mt-2 max-w-sm">{t.services.maintDesc} C${MAINTENANCE_SERVICES[0].price}.</p>
                <span className="inline-flex items-center gap-2 mt-6 font-heading font-bold group-hover:gap-3 transition-all">{t.common.seePricing} <ArrowRight size={18} /></span>
              </Link>
            </motion.div>
          </RevealStagger>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="relative bg-brand-gray text-white py-20 overflow-hidden">
        <Reveal className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="font-heading font-black text-3xl sm:text-4xl"><Typewriter speed={34} segments={[{ text: t.cta.title }]} /></h2>
          <p className="text-white/80 mt-3">{t.cta.subtitle}</p>
          <DepositButtons className="justify-center mt-7" />
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
